import { useState } from 'react';
import { Printer } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/helpers';
import { getSchoolInfo, schoolHeader } from '../../utils/printHelper';

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

function formatTime12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${ampm}`;
}

function HallTicket() {
  const { exams, examTimetable, students, classes, subjects, parents, feeStructures, feePayments, staff } = useApp();
  const { currentUser } = useAuth();
  const isStudentRole = currentUser?.role === 'student';
  const isParentRole = currentUser?.role === 'parent';
  const isTeacher = currentUser?.role === 'teacher';
  const isRestricted = isStudentRole || isParentRole;
  const teacherProfile = isTeacher ? staff.find(s => s.id === currentUser?.profileId) : null;
  const availableClasses = isTeacher
    ? classes.filter(c => (teacherProfile?.classIds || []).includes(c.id))
    : classes;

  const [selectedExam, setSelectedExam] = useState(exams[0]?.id || '');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');

  const exam = exams.find(e => e.id === selectedExam);

  // Student role: only their own profile; Parent role: only their children; others: all active
  const studentList = isStudentRole
    ? students.filter(s => s.id === currentUser?.profileId)
    : isParentRole
      ? students.filter(s => s.status === 'active' && s.parentId === currentUser?.profileId)
      : students.filter(s =>
          s.status === 'active' &&
          availableClasses.some(c => c.id === s.classId) &&
          (!selectedClass || s.classId === selectedClass)
        );

  const getFeeBalance = (student) => {
    const structure = feeStructures.find(f => f.classId === student.classId);
    if (!structure) return null;
    const totalDue = (Number(structure.totalAmount) || 0) + (Number(student.previousYearBalance) || 0);
    const totalPaid = feePayments
      .filter(p => p.studentId === student.id)
      .reduce((sum, p) => sum + (p.amount || 0), 0);
    return totalDue - totalPaid;
  };

  const buildTicketData = (student) => {
    const cls = classes.find(c => c.id === student.classId);
    const parent = parents?.find(p => p.id === student.parentId);
    const entries = examTimetable
      .filter(e => e.examId === selectedExam && (e.classIds || []).includes(student.classId))
      .sort((a, b) => a.date.localeCompare(b.date));
    const feeBalance = getFeeBalance(student);
    return { student, cls, parent, entries, feeBalance };
  };

  const printSingle = async (student) => {
    const school = await getSchoolInfo();
    const { cls, parent, entries, feeBalance } = buildTicketData(student);
    const win = window.open('', '_blank', 'width=800,height=700');
    win.document.open();
    win.document.write(buildPrintHtml([{ student, cls, parent, entries, feeBalance }], school));
    win.document.close();
  };

  const printAll = async () => {
    const school = await getSchoolInfo();
    const tickets = studentList.map(buildTicketData);
    const win = window.open('', '_blank', 'width=800,height=700');
    win.document.open();
    win.document.write(buildPrintHtml(tickets, school));
    win.document.close();
  };

  const buildPrintHtml = (tickets, school = { name: 'Smart Kids Future School', address: '', phone: '' }) => {
    // Group into pairs for 2-per-A4-page
    const pairs = [];
    for (let i = 0; i < tickets.length; i += 2) pairs.push(tickets.slice(i, i + 2));

    const renderTicket = ({ student, cls, parent, entries, feeBalance }) => {
      const timetableRows = entries.map((e) => {
        const sub = subjects.find(s => s.id === e.subjectId);
        const dateObj = new Date(e.date + 'T00:00:00');
        const day = DAYS[dateObj.getDay()];
        return `<tr>
          <td><strong>${formatDate(e.date)}</strong></td>
          <td>${day}</td>
          <td>${formatTime12(e.startTime)} – ${formatTime12(e.endTime)}</td>
          <td><strong>${sub?.name || '—'}</strong></td>
          <td class="sig-cell"></td>
        </tr>`;
      }).join('');

      return `
      <div class="ticket">
        <div class="school-header">
          ${schoolHeader(school)}
          <div class="school-sub" style="font-size:9px;color:#555;margin-top:2px">Academic Year : ${exam?.academicYear || '2025-2026'}</div>
        </div>
        <div class="student-strip">
          <div class="strip-left">
            <div class="srow"><span class="slbl">NAME :</span><span class="sval">${(student.firstName + ' ' + student.lastName).toUpperCase()}</span></div>
            <div class="srow"><span class="slbl">FATHER NAME :</span><span class="sval">${(parent?.name || '—').toUpperCase()}</span></div>
          </div>
          <div class="strip-center">
            <div class="admit-title">ADMIT CARD</div>
            <div class="admit-dates">(${(exam?.name || 'EXAMINATION').toUpperCase()})</div>
          </div>
          <div class="strip-right">
            <div class="srow"><span class="slbl">CLASS :</span><span class="sval">${cls ? `${cls.name} - ${cls.section}`.toUpperCase() : '—'}</span></div>
            <div class="srow"><span class="slbl">ROLL NO :</span><span class="sval">${student.rollNumber || '—'}</span></div>
            <div class="srow"><span class="slbl">ADM NO :</span><span class="sval">${student.admissionNumber || '—'}</span></div>
            <div class="srow"><span class="slbl">FEE BAL :</span><span class="sval fee-bal ${feeBalance === null ? '' : feeBalance <= 0 ? 'fee-clear' : 'fee-due'}">${feeBalance === null ? '—' : feeBalance <= 0 ? 'CLEARED' : '₹' + feeBalance.toLocaleString('en-IN')}</span></div>
          </div>
        </div>
        <div class="table-wrap">
        <table>
          <thead><tr><th>DATE</th><th>DAY</th><th>TIME</th><th>SUBJECT</th><th>INVIGILATOR SIGNATURE</th></tr></thead>
          <tbody>${timetableRows || '<tr><td colspan="5" style="text-align:center;color:#aaa;padding:8px">No schedule found</td></tr>'}</tbody>
        </table>
        </div>
        <div class="instr-title">INSTRUCTIONS</div>
        <ol class="instructions">
          <li>Carry this admit card to every examination session.</li>
          <li>Report 15 minutes before the scheduled time.</li>
          <li>Mobile phones and electronic devices are strictly prohibited.</li>
          <li>Only permitted stationery (pen, pencil, ruler) is allowed.</li>
        </ol>
        <div class="signatures">
          <div class="sig-block"><div class="sig-line"></div><div class="sig-label">Student Signature</div></div>
          <div class="sig-block"><div class="sig-line"></div><div class="sig-label">Parent / Guardian</div></div>
          <div class="sig-block"><div class="sig-line"></div><div class="sig-label">Class Teacher</div></div>
          <div class="sig-block"><div class="sig-line"></div><div class="sig-label">Principal</div></div>
        </div>
      </div>`;
    };

    const ticketPages = pairs.map((pair, pi) => `
      <div class="page ${pi > 0 ? 'page-break' : ''}">
        ${renderTicket(pair[0])}
        ${pair[1] ? `<div class="cut-line">✂ &nbsp; Cut Here</div>${renderTicket(pair[1])}` : ''}
      </div>
    `).join('');

    return `<!DOCTYPE html>
<html><head><title>Admit Card — ${exam?.name}</title>
<link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;900&display=swap" rel="stylesheet">
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Roboto',sans-serif; background:#d1d5db; color:#111; font-size:11px; }

  /* One A4 page holds 2 tickets */
  .page { width:210mm; margin:0 auto; background:#fff; }
  .page-break { page-break-before:always; }

  .ticket { width:100%; border:1.5px solid #111; border-bottom:none; }
  .ticket:last-of-type { border-bottom:1.5px solid #111; }

  /* Cut line between tickets */
  .cut-line { text-align:center; font-size:9px; color:#888; padding:3px 0; border-top:1px dashed #aaa; border-bottom:1px dashed #aaa; background:#f9fafb; letter-spacing:1px; }

  /* School Header */
  .school-header { padding:7px 14px 6px; border-bottom:1.5px solid #111; }
  .admit-title { display:inline-block; font-size:13px; font-weight:900; letter-spacing:0.5px; text-align:center; line-height:1.4; }
  .admit-dates { font-size:12px; font-weight:700; color:#111; text-align:center; margin-top:2px; }

  /* Student strip */
  .student-strip { position:relative; display:flex; align-items:center; gap:0; padding:6px 12px 7px; border-bottom:1.5px solid #111; }
  .strip-left { flex:1; display:flex; flex-direction:column; gap:3px; }
  .strip-center { position:absolute; left:50%; transform:translateX(-50%); text-align:center; }
  .strip-right { display:flex; flex-direction:column; gap:3px; align-items:flex-start; border-left:1px solid #ddd; padding-left:12px; }
  .srow { display:flex; align-items:baseline; gap:5px; font-size:10px; }
  .slbl { font-weight:700; white-space:nowrap; }
  .sval { font-weight:700; text-decoration:underline; }

  /* Table */
  .table-wrap { padding:0 12px; }
  table { width:100%; border-collapse:collapse; }
  thead th { background:#f1f5f9; font-weight:700; font-size:9px; padding:4px 7px; text-align:left; border:1px solid #ccc; }
  td { padding:4px 7px; font-size:10px; border:1px solid #ddd; }
  .sig-cell { width:110px; min-width:90px; }

  /* Instructions */
  .instr-title { font-size:9px; font-weight:700; padding:4px 12px 2px; text-transform:uppercase; letter-spacing:0.5px; }
  .instructions { padding:2px 12px 3px 26px; color:#333; line-height:1.55; font-size:8.5px; }

  /* Signatures */
  .signatures { display:flex; justify-content:space-between; padding:10px 14px 6px; border-top:1.5px solid #111; margin-top:6px; }
  .sig-block { text-align:center; flex:1; }
  .sig-line { width:80%; margin:0 auto 3px; border-top:1px solid #555; }
  .sig-label { font-size:9px; color:#444; }
  .fee-clear { color:#16a34a; }
  .fee-due { color:#dc2626; }

  /* Footer */
  .footer-note { text-align:center; font-size:8.5px; color:#aaa; padding:4px; border-top:1px dashed #ddd; }

  /* Print bar */
  .print-bar { text-align:center; padding:12px; background:#d1d5db; position:sticky; top:0; z-index:99; }
  @media print {
    .print-bar { display:none; }
    body { background:#fff; }
    .page { width:100%; margin:0; }
    @page { size:A4; margin:3mm; }
  }
</style></head><body>
<div class="print-bar">
  <button onclick="window.print()" style="background:linear-gradient(135deg,#1e3a8a,#3730a3);color:#fff;border:none;padding:10px 32px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;font-family:'Roboto',sans-serif">
    🖨&nbsp; Print Hall Ticket${tickets.length > 1 ? 's' : ''}
  </button>
  <span style="margin-left:14px;font-size:12px;color:#555">${exam?.name} — ${tickets.length} student${tickets.length !== 1 ? 's' : ''}</span>
</div>
${ticketPages}
</body></html>`;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Hall Tickets</h1>
        {!isRestricted && studentList.length > 0 && (
          <button onClick={printAll}
            className="flex items-center gap-1.5 px-4 py-1 rounded-lg font-medium text-white"
            style={{ background: 'linear-gradient(135deg,#1e3a8a,#3730a3)' }}>
            <Printer size={14}/> Print All ({studentList.length})
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap items-end">
        <div>
          <label className="block font-medium mb-1">Exam</label>
          <select value={selectedExam} onChange={e => setSelectedExam(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
            {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        {!isRestricted && (
          <>
            <div>
              <label className="block font-medium mb-1">Class</label>
              <select value={selectedClass} onChange={e => { setSelectedClass(e.target.value); setSelectedStudent(''); }}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
                <option value="">All Classes</option>
                {availableClasses.map(c => <option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
              </select>
            </div>
            <div>
              <label className="block font-medium mb-1">Student</label>
              <select value={selectedStudent} onChange={e => setSelectedStudent(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
                <option value="">All Students</option>
                {studentList.map(s => {
                  const c = classes.find(c => c.id === s.classId);
                  return <option key={s.id} value={s.id}>{s.firstName} {s.lastName} — {c ? `${c.name}-${c.section}` : ''}</option>;
                })}
              </select>
            </div>
          </>
        )}
      </div>

      {/* Student list table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="px-4 py-2 text-left font-semibold">#</th>
              <th className="px-4 py-2 text-left font-semibold">Student</th>
              <th className="px-4 py-2 text-left font-semibold">Adm No.</th>
              <th className="px-4 py-2 text-left font-semibold">Class</th>
              <th className="px-4 py-2 text-left font-semibold">Roll No.</th>
              <th className="px-4 py-2 text-left font-semibold">Subjects Scheduled</th>
              <th className="px-4 py-2 text-left font-semibold">Fee Balance</th>
              {!isParentRole && <th className="px-4 py-2 text-center font-semibold">Action</th>}
            </tr>
          </thead>
          <tbody>
            {(selectedStudent ? studentList.filter(s => s.id === selectedStudent) : studentList).map((s, i) => {
              const cls = classes.find(c => c.id === s.classId);
              const count = examTimetable.filter(e => e.examId === selectedExam && (e.classIds || []).includes(s.classId)).length;
              const bal = getFeeBalance(s);
              return (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-1 text-center">{i + 1}</td>
                  <td className="px-4 py-1 font-medium">{s.firstName} {s.lastName}</td>
                  <td className="px-4 py-1">{s.admissionNumber || '—'}</td>
                  <td className="px-4 py-1">{cls ? `${cls.name}-${cls.section}` : '—'}</td>
                  <td className="px-4 py-1">{s.rollNumber || '—'}</td>
                  <td className="px-4 py-1">{count} subject{count !== 1 ? 's' : ''}</td>
                  <td className="px-4 py-1">
                    {bal === null ? <span className="text-gray-400">—</span>
                      : bal <= 0 ? <span className="text-green-600 font-semibold">Cleared</span>
                      : <span className="text-red-600 font-semibold">₹{bal.toLocaleString('en-IN')}</span>}
                  </td>
                  {!isParentRole && (
                    <td className="px-4 py-1 text-center">
                      <button onClick={() => printSingle(s)}
                        className="flex items-center gap-1 px-3 py-0.5 border border-indigo-300 rounded text-indigo-600 hover:bg-indigo-50 mx-auto">
                        <Printer size={12}/> Print
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {studentList.length === 0 && (
              <tr><td colSpan="8" className="py-8 text-center">No students found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
export default HallTicket;
